import type { RevisionEntry } from '@/utils/revisionHistorial';
import { AlertCircle, ChevronDown, ChevronUp, MessageSquare, History } from 'lucide-react';
import { useState } from 'react';

interface Props {
  entries: RevisionEntry[];
  className?: string;
}

function formatFecha(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function HistorialAnteriorMatrizCollapse({ entries, className = '' }: Props) {
  const [abierto, setAbierto] = useState(false);

  if (!entries || entries.length === 0) return null;

  // Encontrar el último rechazo o mensaje relevante
  const sorted = [...entries].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  const ultimoMensaje = sorted[sorted.length - 1];
  const previos = sorted.slice(0, -1);

  return (
    <div className={`mt-1.5 rounded-lg border border-amber-200 bg-amber-50/40 px-3 py-2 shadow-xs ${className}`}>
      <div className="flex flex-col gap-1">
        <div className="flex items-start gap-1.5">
          <History className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-amber-900">
              <span className="font-semibold block mb-0.5 text-amber-800">
                💬 Conversación de Asesoría Anterior (Ronda {ultimoMensaje.ronda}):
              </span>
              <span className="italic whitespace-pre-line">
                "{ultimoMensaje.observacion ? ultimoMensaje.observacion : 'Sin comentario'}"
              </span>
              <span className="text-amber-600/70 text-[10px]">
                {' '}
                · {ultimoMensaje.adminNombre || 'Admin'} · {formatFecha(ultimoMensaje.fecha)}
              </span>
            </div>
          </div>
        </div>

        {previos.length > 0 && (
          <button
            type="button"
            onClick={() => setAbierto((v) => !v)}
            className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800 hover:text-amber-950 hover:underline self-start cursor-pointer"
          >
            {abierto ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {abierto ? 'Ocultar mensajes anteriores' : `Ver conversación completa anterior (${previos.length})`}
          </button>
        )}
      </div>

      {abierto && (
        <ol className="mt-2 border-t border-amber-200/60 pt-2 space-y-1.5">
          {previos.map((e) => {
            const esAsesor = e.estado === 'RESPUESTA_ASESOR' || e.estado === 'CORREGIDO';
            return (
              <li key={e.id} className="flex items-start gap-1.5 text-[10px]">
                {esAsesor ? (
                  <MessageSquare className="w-3 h-3 mt-0.5 shrink-0 text-blue-600" />
                ) : (
                  <AlertCircle className="w-3 h-3 mt-0.5 shrink-0 text-amber-700" />
                )}
                <span className="text-amber-900 whitespace-pre-line text-[10px]">
                  <span className="font-semibold" style={{ color: esAsesor ? '#2563eb' : '#b45309' }}>
                    {esAsesor ? 'Asesor' : `Admin (${e.adminNombre || 'Revisión'})`}
                  </span>
                  {e.observacion ? `: "${e.observacion}"` : ' (sin comentario)'}
                  <span className="text-amber-600/50"> · {formatFecha(e.fecha)}</span>
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
